import React, { useMemo } from 'react';
import { Container, Row, Col } from 'reactstrap';
import { CellContext } from '@tanstack/react-table';
import { DataTable } from 'components/DataTable';
import { DataTableColumn, TableQueryParams, PaginatedResponse } from 'types/table.types';
import { useShop } from 'context/shopContext';
import { API_ENDPOINTS } from 'http-client/httpConstants';
import { DataTableApiService } from 'services/api/tableApi';
import { formatRelativeDate } from 'utils/dateFormatter';
import { fmt } from 'utils/common';

// ─── Type ─────────────────────────────────────────────────────────────────────

interface LedgerEntry {
  id: string;
  shopId: string;
  accountType: string;
  debit: number;
  credit: number;
  description: string;
  referenceType: string;
  referenceId: string;
  createdAt: string;
  // These fields to ask from backend — can be added later
  invoiceNumber?: string;   // e.g. "INV-20260506090556-9598" — extract from description for now
  partyName?: string;       // customer or supplier name
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Extracts invoice number from description string like:
 * "Sale #INV-20260506090556-9598 — revenue"
 */
function extractInvoiceNumber(description: string): string | null {
  const match = description.match(/#([A-Z0-9-]+)/);
  return match?.[1] ?? null;
}

/**
 * Returns a plain-English explanation of what this ledger entry means.
 * Written for a common man — no accounting jargon.
 */
function getPlainEnglishDescription(entry: LedgerEntry): {
  what: string;
  icon: string;
  moneyDirection: 'in' | 'out' | 'neutral';
  amount: number;
} {
  const { accountType, referenceType, debit, credit } = entry;
  const amount = debit > 0 ? debit : credit;

  // Cash account
  if (accountType === 'Cash') {
    if (debit > 0) return { what: 'Customer paid cash',         icon: '💵', moneyDirection: 'in',  amount };
    if (credit > 0) return { what: 'Refund paid to customer',   icon: '↩',  moneyDirection: 'out', amount };
  }

  // Revenue account
  if (accountType === 'Revenue') {
    if (credit > 0) return { what: 'Sale income recorded',      icon: '📈', moneyDirection: 'in',  amount };
    if (debit > 0)  return { what: 'Sale income reversed',      icon: '📉', moneyDirection: 'out', amount };
  }

  // Inventory account
  if (accountType === 'Inventory') {
    if (debit > 0)  return { what: 'Stock purchased from supplier', icon: '📦', moneyDirection: 'out', amount };
    if (credit > 0) return { what: 'Stock value reduced',           icon: '📦', moneyDirection: 'out', amount };
  }

  // Payable account
  if (accountType === 'Payable') {
    if (credit > 0) return { what: 'We owe this to supplier',   icon: '🏦', moneyDirection: 'out', amount };
    if (debit > 0)  return { what: 'Supplier payment settled',  icon: '✅', moneyDirection: 'in',  amount };
  }

  // Discount
  if (accountType === 'Discount') {
    if (debit > 0)  return { what: 'Discount given to customer', icon: '🎁', moneyDirection: 'out', amount };
  }

  // Tax
  if (accountType === 'Tax') {
    if (credit > 0) return { what: 'Tax collected',             icon: '🧾', moneyDirection: 'neutral', amount };
    if (debit > 0)  return { what: 'Tax reversed',              icon: '🧾', moneyDirection: 'neutral', amount };
  }

  return { what: entry.description || 'Ledger entry', icon: '📋', moneyDirection: 'neutral', amount };
}

/**
 * What caused this entry — human readable.
 */
function getSourceLabel(referenceType: string): { label: string; color: string; bg: string } {
  const map: Record<string, { label: string; color: string; bg: string }> = {
    SellInvoice:        { label: 'Sale',            color: '#059669', bg: '#ecfdf5' },
    PurchaseInvoice:    { label: 'Purchase',         color: '#2563eb', bg: '#eff6ff' },
    SellAdjustment:     { label: 'Sale correction',  color: '#d97706', bg: '#fffbeb' },
    PurchaseAdjustment: { label: 'Purchase correction', color: '#7c3aed', bg: '#f5f3ff' },
  };
  return map[referenceType] ?? { label: referenceType, color: '#6b7280', bg: '#f3f4f6' };
}

const ACCOUNT_META: Record<string, { icon: string; simpleLabel: string; color: string; bg: string }> = {
  Cash:      { icon: '💵', simpleLabel: 'Cash',         color: '#059669', bg: '#ecfdf5' },
  Revenue:   { icon: '📈', simpleLabel: 'Income',       color: '#2563eb', bg: '#eff6ff' },
  Inventory: { icon: '📦', simpleLabel: 'Stock',        color: '#7c3aed', bg: '#f5f3ff' },
  Payable:   { icon: '🏦', simpleLabel: 'Supplier debt', color: '#dc2626', bg: '#fef2f2' },
  Discount:  { icon: '🎁', simpleLabel: 'Discount',     color: '#d97706', bg: '#fffbeb' },
  Tax:       { icon: '🧾', simpleLabel: 'Tax',          color: '#ea580c', bg: '#fff7ed' },
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const AccountPill: React.FC<{ type: string }> = ({ type }) => {
  const m = ACCOUNT_META[type] ?? { icon: '📋', simpleLabel: type, color: '#6b7280', bg: '#f3f4f6' };
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '2px 8px', borderRadius: 20,
      fontSize: 11, fontWeight: 600,
      background: m.bg, color: m.color,
      whiteSpace: 'nowrap', lineHeight: 1.7,
    }}>
      {m.icon} {m.simpleLabel}
    </span>
  );
};

const SourcePill: React.FC<{ type: string }> = ({ type }) => {
  const m = getSourceLabel(type);
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center',
      padding: '2px 8px', borderRadius: 20,
      fontSize: 11, fontWeight: 600,
      background: m.bg, color: m.color,
      whiteSpace: 'nowrap', lineHeight: 1.7,
    }}>
      {m.label}
    </span>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

const LedgerEntriesList: React.FC = () => {
  const { shop } = useShop();
  const shopId = shop?.id ?? '';

  const columns = useMemo<DataTableColumn<LedgerEntry>[]>(() => [

    // ── What happened — plain English with icon ───────────────────────────────
    {
      accessorKey: 'description',
      id: 'whatHappened',
      header: 'What happened?',
      enableSorting: false,
      enableFiltering: true,
      cell: (info: CellContext<LedgerEntry, unknown>) => {
        const entry = info.row.original;
        const { what, icon } = getPlainEnglishDescription(entry);
        const invoiceNum = extractInvoiceNumber(entry.description);

        return (
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 9 }}>
            {/* Icon bubble */}
            <div style={{
              width: 30, height: 30, borderRadius: 8, flexShrink: 0,
              background: '#f3f4f6', display: 'flex',
              alignItems: 'center', justifyContent: 'center', fontSize: 15,
            }}>
              {icon}
            </div>
            <div style={{ minWidth: 0 }}>
              {/* Plain English label */}
              <div style={{ fontSize: 13, fontWeight: 600, color: '#0d0f12' }}>
                {what}
              </div>
              {/* Invoice number if found */}
              {invoiceNum && (
                <div style={{
                  fontSize: 11, color: '#9ca3af', marginTop: 1,
                  fontFamily: "'JetBrains Mono','Fira Code',monospace",
                }}>
                  {invoiceNum}
                </div>
              )}
            </div>
          </div>
        );
      },
    },

    // ── Account — which bucket does this affect ───────────────────────────────
    {
      accessorKey: 'accountType',
      id: 'accountType',
      header: 'Account',
      enableSorting: true,
      enableFiltering: true,
      cell: (info: CellContext<LedgerEntry, unknown>) => (
        <AccountPill type={info.getValue() as string} />
      ),
    },

    // ── Money direction — the key insight ────────────────────────────────────
    {
      accessorKey: 'debit',
      id: 'moneyFlow',
      header: 'Amount',
      enableSorting: true,
      enableFiltering: false,
      cell: (info: CellContext<LedgerEntry, unknown>) => {
        const entry = info.row.original;
        const { moneyDirection, amount } = getPlainEnglishDescription(entry);

        if (amount === 0) return <span style={{ color: '#9ca3af' }}>—</span>;

        const isIn      = moneyDirection === 'in';
        const isOut     = moneyDirection === 'out';
        const color     = isIn ? '#059669' : isOut ? '#dc2626' : '#374151';
        const bg        = isIn ? '#ecfdf5' : isOut ? '#fef2f2' : '#f9fafb';
        const prefix    = isIn ? '+' : isOut ? '−' : '';
        const label     = isIn ? 'Came in' : isOut ? 'Went out' : '';

        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              padding: '3px 9px', borderRadius: 7,
              background: bg,
            }}>
              <span style={{
                fontFamily: "'JetBrains Mono','Fira Code',monospace",
                fontSize: 13, fontWeight: 700, color,
              }}>
                {prefix}{fmt(amount)}
              </span>
            </div>
            {label && (
              <span style={{ fontSize: 10.5, color, fontWeight: 500, paddingLeft: 4 }}>
                {label}
              </span>
            )}
          </div>
        );
      },
    },

    // ── Source — what caused this ─────────────────────────────────────────────
    {
      accessorKey: 'referenceType',
      id: 'referenceType',
      header: 'Caused by',
      enableSorting: true,
      enableFiltering: true,
      cell: (info: CellContext<LedgerEntry, unknown>) => (
        <SourcePill type={info.getValue() as string} />
      ),
    },

    // ── Debit / Credit — for accountants ─────────────────────────────────────
    // Shown as a compact pair so accountants can verify, but not prominent
    {
      accessorKey: 'credit',
      id: 'debitCredit',
      header: 'DR / CR',
      enableSorting: false,
      enableFiltering: false,
      cell: (info: CellContext<LedgerEntry, unknown>) => {
        const entry = info.row.original;
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {entry.debit > 0 && (
              <div style={{ fontSize: 11.5, display: 'flex', gap: 4 }}>
                <span style={{ color: '#9ca3af', minWidth: 18 }}>DR</span>
                <span style={{
                  fontFamily: 'monospace', fontWeight: 600, color: '#059669',
                }}>
                  {fmt(entry.debit)}
                </span>
              </div>
            )}
            {entry.credit > 0 && (
              <div style={{ fontSize: 11.5, display: 'flex', gap: 4 }}>
                <span style={{ color: '#9ca3af', minWidth: 18 }}>CR</span>
                <span style={{
                  fontFamily: 'monospace', fontWeight: 600, color: '#dc2626',
                }}>
                  {fmt(entry.credit)}
                </span>
              </div>
            )}
          </div>
        );
      },
    },

    // ── Date ─────────────────────────────────────────────────────────────────
    {
      accessorKey: 'createdAt',
      id: 'createdAt',
      header: 'When?',
      enableSorting: true,
      enableFiltering: false,
      cell: (info: CellContext<LedgerEntry, unknown>) => {
        const { text, isStale } = formatRelativeDate(info.getValue() as string);
        return (
          <span style={{ fontSize: 12, color: isStale ? '#dc2626' : '#6b7280' }}>
            {text}
          </span>
        );
      },
    },

  ], []);

  const handleFetchData = async (
    params: TableQueryParams,
  ): Promise<PaginatedResponse<LedgerEntry>> => {
    return DataTableApiService.fetchPaginatedData<LedgerEntry>(
      API_ENDPOINTS.TENANT.LEDGERS.PAGINATED.replace(':shopId', shopId),
      { ...params, filters: { ...params.filters, shopId } },
    );
  };

  return (
    <div className="page-content">
      <Container fluid>
        <Row>
          <Col lg="12">

            {/* Header */}
            <div style={{ marginBottom: 16 }}>
              <h5 style={{ margin: 0, fontWeight: 600 }}>Financial ledger</h5>
              <p style={{ margin: 0, fontSize: 12, color: '#6b7280', marginTop: 3 }}>
                A permanent record of every money movement in your pharmacy.
                These entries are <strong>never edited or deleted</strong> — full audit trail.
              </p>
            </div>

            {/* Quick legend */}
            <div style={{
              display: 'flex', gap: 16, flexWrap: 'wrap',
              padding: '8px 12px', background: '#f9fafb',
              border: '1px solid #e5e7eb', borderRadius: 8,
              marginBottom: 14, fontSize: 12, color: '#6b7280',
            }}>
              <span>💵 <strong style={{ color: '#059669' }}>Green</strong> = money came into your shop</span>
              <span>↩ <strong style={{ color: '#dc2626' }}>Red</strong> = money went out of your shop</span>
              <span>📦 <strong style={{ color: '#7c3aed' }}>Purple</strong> = stock / inventory value</span>
              <span>🏦 <strong style={{ color: '#dc2626' }}>Payable</strong> = amount still owed to suppliers</span>
            </div>

            <DataTable
              columns={columns}
              queryKey={API_ENDPOINTS.TENANT.LEDGERS.PAGINATED.replace(':shopId', shopId)}
              onFetchData={handleFetchData}
              enableSearch
              enableFiltering
              enablePagination
              enableSorting
              enableColumnVisibility
              enableColumnReorder={false}
              enableRowSelection={false}
              enableBulkActions={false}
              enableExport
              pageSizeOptions={[10, 20, 50, 100]}
              defaultPageSize={20}
              columnVisibilityStorageKey="ledger_entries_visibility"
              columnOrderStorageKey="ledger_entries_order"
              title=""
              emptyMessage="No ledger entries found."
              striped
              hover
              bordered
              responsive
            />

          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default LedgerEntriesList;